/**
 * A chat snap: a photo or clip each member opens for ten seconds, and can go
 * back to for its first day.
 *
 * Pure, and free of React and Firebase, because the service worker imports it
 * too, to leave snaps out of its media cache.
 *
 * What "gone" means, precisely, because a web app cannot promise more. Once
 * you have looked, the app never shows it to you again, and neither the phone's
 * HTTP cache nor the service worker's media cache keeps a copy. The photo stays
 * for the family members who have not looked yet, until SNAP_LIFETIME_MS,
 * when the app stops showing it to anyone and a lifecycle rule deletes the
 * bytes. Screenshots cannot be stopped or detected from a browser.
 */
import { PREFIX, SNAP_LIFETIME_MS, SNAP_REPLAY_MS } from '../../shared/constants';
import type { MessageDoc } from '../../shared/types';

/** Whether what it carries is a clip. */
export function isVideoSnap(data: Pick<MessageDoc, 'media'>): boolean {
  return data.media?.[0]?.kind === 'video';
}

/** A stored snap, by its R2 key. */
export function isSnapKey(key: string): boolean {
  return key.startsWith(PREFIX.snaps);
}

/**
 * A request for one, by its gateway url. The key's slashes are kept as they
 * are when the url is built, so the prefix reads straight off the path.
 */
export function isSnapUrl(url: URL): boolean {
  return url.pathname.startsWith(`/media/${PREFIX.snaps}`);
}

function sentAt(data: Pick<MessageDoc, 'createdAt'>): number | null {
  return (data.createdAt as { toMillis?: () => number } | null)?.toMillis?.() ?? null;
}

/** Past its 48 hours, opened or not. One still being written has no age yet. */
export function isExpired(data: Pick<MessageDoc, 'createdAt'>, now: number): boolean {
  const at = sentAt(data);
  return at !== null && now - at >= SNAP_LIFETIME_MS;
}

/**
 * Whether a snap already looked at, or your own, can be opened again: for its
 * first day, while it is still there to open.
 */
export function canReplay(
  data: Pick<MessageDoc, 'createdAt' | 'deletedAt' | 'status' | 'media'>,
  now: number,
): boolean {
  const at = sentAt(data);
  return (
    at !== null &&
    now - at < SNAP_REPLAY_MS &&
    !data.deletedAt &&
    data.status !== 'processing' &&
    data.status !== 'failed' &&
    Boolean(data.media?.[0])
  );
}

/**
 * What a snap is to the person looking at the thread.
 *
 * - `unopened`: somebody else's, not looked at, still inside its lifetime --
 *   the only state in which it can be held open.
 * - `opened`: you have had your look.
 * - `expired`: its lifetime is over before you looked.
 * - `gone`: deleted by the sender.
 * - `sending`: a clip still being processed, which nobody can open yet.
 * - `failed`: a clip the transcoder could not make anything of.
 * - `yours`: you sent it; you see who has looked.
 *
 * `opened` and `yours` can still be opened for SNAP_REPLAY_MS -- see
 * canReplay. They keep their state and their look; it is only a second view.
 */
export type SnapState =
  | 'unopened'
  | 'opened'
  | 'expired'
  | 'gone'
  | 'sending'
  | 'failed'
  | 'yours';

export function snapStateFor(data: MessageDoc, uid: string, now: number): SnapState {
  if (data.deletedAt) return 'gone';
  // Before whose it is: a clip on its way is on its way for everybody.
  if (data.status === 'processing') return 'sending';
  if (data.status === 'failed') return 'failed';
  if (data.authorUid === uid) return 'yours';
  if ((data.viewedBy ?? []).includes(uid)) return 'opened';
  if (isExpired(data, now)) return 'expired';
  return 'unopened';
}

/** How long a photo snap stays open, not counting time held paused. */
export const SNAP_VIEW_MS = 10_000;

/** Shorter than this, a press on an open snap is a tap and closes it. */
export const SNAP_TAP_MS = 250;

/**
 * How long a closing snap takes to zoom out and go. Very quick, and a small
 * move: flying back into its bubble read as a glide you sat through, at 200ms
 * and still at 80. A short zoom in place is over before it is noticed as
 * anything but smooth -- 70ms was better, and Carter asked for faster still.
 * About three frames. Kept in step with theme.css.
 */
export const SNAP_CLOSE_MS = 45;

/** Pulling an open snap down: past this far, or this fast, it closes. */
export const SNAP_PULL_CLOSE_PX = 90;
export const SNAP_PULL_CLOSE_SPEED = 0.6;
/** Less than this down is still a tap or a hold. */
export const SNAP_PULL_SLOP_PX = 8;

/**
 * Until when this person can still open a snap: its full lifetime while it
 * waits for them, its day of replays once they have looked or if it is
 * theirs. Never, for one deleted; not yet decided -- so not over -- for one
 * still being made or not yet stamped by the server.
 */
export function openableUntil(
  data: Pick<MessageDoc, 'createdAt' | 'deletedAt' | 'status' | 'authorUid' | 'viewedBy'>,
  uid: string,
): number {
  if (data.deletedAt) return -Infinity;
  if (data.status === 'processing') return Infinity;
  const at = sentAt(data);
  if (at === null) return Infinity;
  // Never openable, but "did not send" is news to whoever sent it: it keeps
  // its line for a snap's lifetime before it shrinks.
  if (data.status === 'failed') return at + SNAP_LIFETIME_MS;
  const seen = data.authorUid === uid || (data.viewedBy ?? []).includes(uid);
  return at + (seen ? SNAP_REPLAY_MS : SNAP_LIFETIME_MS);
}

/**
 * When a row of snaps -- one on its own, or an exchange -- is spent for this
 * person: the moment the last thing in it they could open stops being
 * openable. Spent rows shrink to a small line; nothing is removed.
 */
export function spentAt(
  snaps: Pick<MessageDoc, 'createdAt' | 'deletedAt' | 'status' | 'authorUid' | 'viewedBy'>[],
  uid: string,
): number {
  return Math.max(...snaps.map((snap) => openableUntil(snap, uid)));
}

/** How many squares a row draws before it says "+N" instead. */
export const SNAP_MARKS_SHOWN = 8;
/** How many faces an exchange row shows before "+N". */
export const SNAP_FACES_SHOWN = 3;

/**
 * Who is in an exchange, as a line: every name up to three, then the first
 * two and how many others -- "Mom, Aana and 9 others" -- so a snap the whole
 * family joined is still one short line.
 */
export function namesLine(names: string[]): string {
  if (names.length <= 2) return names.join(' and ');
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`;
  const rest = names.length - 2;
  return `${names[0]}, ${names[1]} and ${rest} others`;
}
